/**
 * Send KOT (Kitchen Order Ticket) to a Bluetooth thermal printer.
 * Supports:
 * - Web Serial API: Bluetooth Classic SPP (e.g. NETUM NT-1809DD/NT-1809). Pair the printer in OS first, then select it as a serial port.
 * - Web Bluetooth API: BLE thermal printers (GATT services).
 * Requires HTTPS and user gesture. Chrome 117+ for Serial over Bluetooth.
 */

// ESC/POS commands
const ESC = 0x1b;
const LF = 0x0a;

const ESC_INIT = [ESC, 0x40];
const ESC_ALIGN_LEFT = [ESC, 0x61, 0x00];
const ESC_ALIGN_CENTER = [ESC, 0x61, 0x01];
const ESC_LF = [LF];
// Reduced line spacing (default ~30 dots) for more compact KOT
const ESC_LINE_SPACING_24 = [ESC, 0x33, 24];
// Font B = smaller (9x17) vs Font A (12x24)
const ESC_FONT_B = [ESC, 0x4d, 1];
// Code page CP850 – required for £ (0x9C) to render on thermal printers
const ESC_CODE_PAGE_CP850 = [ESC, 0x74, 2];
// ESC ! n – select print mode bits: bit0 Font B, bit4 double height, bit5 double width
const CHAR_SIZE_NORMAL = 0x01;
const CHAR_SIZE_DOUBLE = 0x01 | 0x10 | 0x20;
const escCharSize = (n) => [ESC, 0x21, n];

// BLE GATT UUIDs (for BLE thermal printers only; NETUM NT-1809DD uses SPP, not BLE)
const SERVICE_THERMAL = '0000ae30-0000-1000-8000-00805f9b34fb';
const CHAR_WRITE_THERMAL = '0000ae01-0000-1000-8000-00805f9b34fb';
const SERVICE_NORDIC_UART = '0000ffe0-0000-1000-8000-00805f9b34fb';
const CHAR_WRITE_NORDIC = '0000ffe1-0000-1000-8000-00805f9b34fb';
const SERVICE_NETUM = '000018f0-0000-1000-8000-00805f9b34fb';
const CHAR_NETUM = '00002af1-0000-1000-8000-00805f9b34fb';

const CHUNK_SIZE = 200;
const SERIAL_BAUD_RATE = 115200;

// Cached connections – reuse after first connect so we don't ask every time
let cachedSerialPort = null;
let cachedBleConnection = null;

/**
 * Encode a line to CP850 bytes. Maps ASCII 1:1 and £ (U+00A3) to 0x9C so the
 * thermal printer renders the symbol instead of dropping the UTF-8 bytes (which
 * previously showed up as a stray "0").
 */
function encodeCp850(text) {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    if (ch === 0x00a3) {
      bytes[i] = 0x9c;
    } else if (ch < 0x80) {
      bytes[i] = ch;
    } else {
      bytes[i] = 0x3f;
    }
  }
  return bytes;
}

/**
 * Build ESC/POS buffer from KOT plain text.
 * "JUICE PLANET" prints double-size and centered; "Powered by KnockIt Technology"
 * is centered; other lines left-aligned.
 */
export function buildEscPosBuffer(kotText) {
  const lines = kotText.split('\n');
  const buffers = [];

  buffers.push(new Uint8Array(ESC_INIT));
  buffers.push(new Uint8Array(ESC_CODE_PAGE_CP850));
  buffers.push(new Uint8Array(ESC_LINE_SPACING_24));
  buffers.push(new Uint8Array(ESC_FONT_B));

  const centerLines = ['JUICE PLANET', 'Powered by KnockIt Technology'];
  const largeLines = ['JUICE PLANET'];

  for (const line of lines) {
    const trimmed = line.trim();
    const isCenter = centerLines.includes(trimmed);
    const isLarge = largeLines.includes(trimmed);
    buffers.push(new Uint8Array(isCenter ? ESC_ALIGN_CENTER : ESC_ALIGN_LEFT));
    if (isLarge) {
      buffers.push(new Uint8Array(escCharSize(CHAR_SIZE_DOUBLE)));
    }
    buffers.push(encodeCp850(line + '\n'));
    if (isLarge) {
      buffers.push(new Uint8Array(escCharSize(CHAR_SIZE_NORMAL)));
    }
    buffers.push(new Uint8Array(ESC_LF));
  }

  // Bottom padding so footer is not cut off at the tear edge
  for (let i = 0; i < 4; i++) {
    buffers.push(new Uint8Array(ESC_LF));
  }

  const totalLen = buffers.reduce((acc, b) => acc + b.length, 0);
  const out = new Uint8Array(totalLen);
  let offset = 0;
  for (const b of buffers) {
    out.set(b, offset);
    offset += b.length;
  }
  return out;
}

/**
 * Get or request a serial port (cached after first connection so we don't ask every time).
 */
async function getOrRequestSerialPort() {
  if (cachedSerialPort) {
    return cachedSerialPort;
  }
  if (!navigator.serial) {
    return null;
  }
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate: SERIAL_BAUD_RATE });
  cachedSerialPort = port;
  return port;
}

/**
 * Print via Web Serial API (Bluetooth SPP). Use for NETUM NT-1809DD/NT-1809 and similar.
 * Printer must be paired in OS first. Connection is cached after first connect.
 */
async function printViaSerial(buffer) {
  if (!navigator.serial) {
    return false;
  }
  try {
    const port = await getOrRequestSerialPort();
    if (!port) return false;
    const writer = port.writable.getWriter();
    try {
      for (let i = 0; i < buffer.length; i += CHUNK_SIZE) {
        const chunk = buffer.subarray(i, Math.min(i + CHUNK_SIZE, buffer.length));
        await writer.write(chunk);
      }
    } finally {
      writer.releaseLock();
    }
    return true;
  } catch (e) {
    cachedSerialPort = null;
    throw e;
  }
}

/**
 * Write data to the characteristic in chunks (BLE MTU limit).
 */
async function writeInChunks(characteristic, data) {
  for (let i = 0; i < data.length; i += CHUNK_SIZE) {
    const chunk = data.subarray(i, Math.min(i + CHUNK_SIZE, data.length));
    await characteristic.writeValue(chunk);
  }
}

/**
 * Connect to a BLE thermal printer and return the write characteristic.
 */
async function connectToBlePrinter() {
  if (!navigator.bluetooth) {
    throw new Error('Bluetooth is not supported in this browser.');
  }
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ namePrefix: 'NETUM' }, { namePrefix: 'NT-' }],
    optionalServices: [SERVICE_NETUM, SERVICE_THERMAL, SERVICE_NORDIC_UART],
  });
  const server = await device.gatt.connect();
  let characteristic = null;
  try {
    const service = await server.getPrimaryService(SERVICE_NETUM);
    characteristic = await service.getCharacteristic(CHAR_NETUM);
  } catch {
    try {
      const service = await server.getPrimaryService(SERVICE_THERMAL);
      characteristic = await service.getCharacteristic(CHAR_WRITE_THERMAL);
    } catch {
      try {
        const service = await server.getPrimaryService(SERVICE_NORDIC_UART);
        characteristic = await service.getCharacteristic(CHAR_WRITE_NORDIC);
      } catch {
        if (device.gatt?.connected) device.gatt.disconnect();
        throw new Error(
          'Could not find print service on this device. Try pairing the printer first.',
        );
      }
    }
  }
  return { device, characteristic };
}

/**
 * Get or create BLE printer connection (cached after first connect so we don't ask every time).
 */
async function getOrRequestBleConnection() {
  if (cachedBleConnection?.device?.gatt?.connected) {
    return cachedBleConnection;
  }
  cachedBleConnection = null;
  const conn = await connectToBlePrinter();
  cachedBleConnection = conn;
  conn.device.addEventListener('gattserverdisconnected', () => {
    cachedBleConnection = null;
  });
  return conn;
}

/**
 * Print KOT text to a Bluetooth thermal printer.
 * Tries Web Serial first (for SPP printers like NETUM NT-1809DD), then Web Bluetooth (BLE printers).
 * @param {string} kotText - Plain text KOT (same as used for browser print).
 * @returns {Promise<void>}
 */
export async function printKotViaBluetooth(kotText) {
  const buffer = buildEscPosBuffer(kotText);

  // 1) Try Web Serial (Bluetooth SPP) – for NETUM NT-1809DD / NT-1809. Pair printer in OS first, then select it as a port.
  if (navigator.serial) {
    try {
      await printViaSerial(buffer);
      return;
    } catch (e) {
      if (e.name === 'NotFoundError') {
        throw new Error(
          'No port selected. Pair your NETUM printer in device Bluetooth settings, then try again.',
        );
      }
      if (e.name === 'SecurityError') {
        throw e;
      }
      throw new Error(
        e?.message ||
          'Serial print failed. Ensure the printer is paired and selected.',
      );
    }
  }

  // 2) Fallback: Web Bluetooth (BLE printers only – not used by NETUM NT-1809DD)
  // Reuse cached connection so we only ask for device the first time
  try {
    const { characteristic } = await getOrRequestBleConnection();
    await writeInChunks(characteristic, buffer);
  } catch (e) {
    cachedBleConnection = null;
    throw e;
  }
}

export default printKotViaBluetooth;
